// Shared composer configuration — the mode list and the symbol palette.
//
// Home and Workspace render the SAME composer in two shapes (a hero card and a docked
// bar), so the two must offer the same things. They did not: Home still listed the
// Gemini 3.7 trial pair as separate "Fast (3.7)" / "Beats (3.7)" entries next to a legacy
// "Fast", while Workspace had already settled on one entry named "Fast" backed by
// mode_fast_37. Picking a mode on Home therefore meant something different from picking
// the identically-named mode one page over. Keeping both lists here is the only way that
// stays true — the previous arrangement was two literals that were never diffed.

// The picker chooses the INPUT as well as the model: two of the three take a document instead
// of a typed topic. PDF and Project are not models — they are multi-step flows with an upload,
// an extraction pass, a selection step and a paywall, and CreateProjectModal already does all of
// that. So they hand off to that flow rather than pretending to be a model: Workspace opens the
// modal in place, Home navigates to /workspace?mode=<value>.
//
// EACH ENTRY IS A NOUN FOR WHAT YOU GET OR GIVE. It was five (Fast, Homework, Lecture, Course),
// then three. "Fast" named a speed tier, which only made sense while HD sat next to it in the
// picker — that went 2026-09-10, so Fast has been a tier with no sibling, and the menu led with
// the one item that described HOW rather than WHAT. That part stands.
//
// HOMEWORK AND LECTURE ARE BACK AS SEPARATE ENTRIES, and the merge that collapsed them into one
// "PDF" is reverted. The argument for merging was that they are one uploader differing by a
// single stored type, so the menu should not ask a question the next screen already asks. What
// that traded away was a click on the most common path in the product, and what it bought was a
// screen whose whole job is to ask which of two things you meant — a question you had already
// answered in your head before you opened the menu. The compensating idea was that the chooser
// had "room to illustrate it"; four rounds of artwork for those two tiles were all rejected,
// which is the clearest possible evidence that the screen had nothing to add.
//
// So both say out loud what they take, and both land straight on the uploader with the type
// already set. THE CHOOSER IS NOT DELETED — it is the only sane answer for a bare DROP, where a
// file arrives having said nothing about what it is (see ?mode=upload in WorkspacePage). It is
// simply no longer on anyone's way to anywhere.
//
// Labels are literal English here, as the others always have been; this list has no `t`.
export const COMPOSER_MODES = [
  { value: "mode_fast_37", label: "Video", input: "text" },
  // HD DROPPED FROM THE PICKER 2026-09-10. Its preset, pricing and admin label are KEPT in
  // settings.py exactly as mode_fast's were when that went, so past HD videos still price and
  // label correctly in the Videos tab.
  // It is also out of SELECTABLE_MODEL_MODES, because that set means "a client may name this
  // mode explicitly" and an entry with no picker behind it is a half-state. HD REMAINS
  // REACHABLE FOR SUBSCRIBERS regardless: apply_model_gating checks SUBSCRIBER_ONLY_MODES
  // *before* the selectable set, so a paid-tier request is still honoured verbatim. What the
  // removal ends is a free or stale client naming it and skipping tier gating.
  // NOTE: PremiumBanner still advertises "HD lessons — 2560x1440 at 60fps" and nothing in the
  // UI can now select HD. That claim needs either a way back in or to come off the plan.
  //
  // THE TWO DOCUMENT ENTRIES, adjacent so they scan as a pair, and carrying BOTH `project` and
  // `upload`. `project` is what skips the chooser — WorkspacePage seeds `type` from it, which
  // is the state CreateProjectModal reads as "I know which kind, show the uploader". `upload`
  // is belt and braces: it is what the chooser consults to drop the two types that cannot read
  // a file, and it stays true of these entries whether or not anything reads it today.
  //
  // The values are "hw" and "lecture" — the same strings that have always been valid in a URL
  // and are persisted on sessions. They moved into this list rather than being duplicated here,
  // so there is still exactly one definition of each.
  { value: "hw", label: "Homework PDF", input: "handoff", project: "hw", upload: true },
  { value: "lecture", label: "Lecture PDF", input: "handoff", project: "lecture", upload: true },
  // `value` STAYS "project" while the label says Project — it was briefly "Course" — and the
  // project type it opens is still `self_study`. Those values are persisted on jobs and read by
  // the routing below; renaming them would be a data migration to change a word on screen.
  { value: "project", label: "Project", input: "handoff", project: "self_study" },
];

// NOT IN THE PICKER, still valid as a URL — which is the only reason this list still exists.
// `?mode=pdf` was the merged entry's value for as long as it shipped, so links and any saved
// tab pointing at it must keep working; it resolves the way it always did, to the chooser, by
// having no `project` key. New surfaces should link to ?mode=hw or ?mode=lecture instead.
// This is why `?mode=` resolves through `resolveMode` rather than searching the picker list.
export const DEEP_LINK_MODES = [
  { value: "pdf", input: "handoff", upload: true },
];

export const resolveMode = (value) =>
  COMPOSER_MODES.find((m) => m.value === value)
  || DEEP_LINK_MODES.find((m) => m.value === value);
/* A LOCAL-ONLY MODE THAT GENERATES NOTHING. It exists to exercise the generating tile, the
   queue card, the countdown and the hand-off to Workspace without spending a credit or a cent
   of model time; it never calls the API, so there is no job, no cost row and no video.

   Gated on the HOSTNAME, not on `import.meta.env.DEV`. DEV is false in anything `vite build`
   produces regardless of --mode, which would have taken the option out of a locally served
   production build — exactly the build you want when measuring whether something is slow
   because of the machine or because of the dev server. A hostname check covers both, and
   localhost is not a hostname any deployed copy of this app is ever served from. */
export const DEV_EMPTY_MODE = "mode_dev_empty";
export const DEV_EMPTY_SECONDS = 60;
/* A FAILURE YOU CAN LOOK AT. The failure UI — the card that stays, the banner, the reason
   line — could previously only be seen by having a real generation die, which means waiting
   for the render fleet to have a bad day. This mode fails on purpose, locally, with the error
   text the Modal client actually produces, so the thing can be designed and checked. Local
   only, same guard as Empty. */
export const DEV_FAIL_MODE = "mode_dev_fail";
export const DEV_FAIL_SECONDS = 6;

export const isLocalHost = () =>
  typeof window !== "undefined"
  && ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);

if (isLocalHost()) {
  COMPOSER_MODES.push({ value: DEV_EMPTY_MODE, label: "Empty (local)", input: "text", dev: true });
  COMPOSER_MODES.push({ value: DEV_FAIL_MODE, label: "Fail (local)", input: "text", dev: true });
}

export const DEFAULT_COMPOSER_MODE = "mode_fast_37";

/* Symbol palette for the composer, Symbolab-style. Grouped because a flat grid of 30
   glyphs is a wall — you scan for "the Greek one" or "the calculus one", not alphabetically.
   Plain Unicode, not LaTeX: this text goes to an LLM as a prompt, and "∫" is understood
   while "\\int" just spends tokens looking like markup. */
export const SYMBOL_GROUPS = [
  { label: "Basic",    items: ["+", "−", "×", "÷", "±", "=", "≠", "≈", "<", ">", "≤", "≥"] },
  { label: "Powers",   items: ["²", "³", "ⁿ", "√", "∛", "^", "_", "|x|", "%", "!", "(", ")"] },
  { label: "Greek",    items: ["π", "θ", "α", "β", "γ", "δ", "Δ", "λ", "μ", "σ", "φ", "ω"] },
  { label: "Calculus", items: ["∫", "∑", "∏", "∂", "∇", "lim", "→", "∞", "dx", "dy", "′", "″"] },
  { label: "Sets",     items: ["∈", "∉", "⊂", "∪", "∩", "∅", "∀", "∃", "ℝ", "ℤ", "ℕ", "ℚ"] },
];
